import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store";
import type { AppDispatch } from "store";
import {
  updateChartPriceRange,
  updateVerticalLineInitialTimestamp,
  updateVisibleCandleCount,
  updateTimeframeMs,
  selectDrawingInstances,
  drawDrawingInstances,
  useDrawingToolDrag,
  timestampToX,
} from "features/drawing-tool/model";
import type { DrawingToolInstance } from "features/drawing-tool/model";
import websocketService from "services/websocket";
import ChartIndicators, { IndicatorType } from "../../ChartIndicators";
import type {
  ZoomRange,
  CrosshairPosition,
  CandleWithFlag,
} from "features/chart";
import {
  COLORS,
  GRID,
  CANDLE_MIN_WIDTH,
  ANIMATION_DURATION,
  RIGHT_PADDING_PERCENT,
  useRightPadding,
  drawGrid,
  drawLine,
  drawCandles,
  drawHistogram,
  drawExpiryLine,
  drawLivePriceLine,
  drawCrosshair,
  Spiner,
  MarketClosedOverlay,
  useChartInteraction,
  useChartWebSocket,
  useCandleOpenTicker,
  useChartAutoScroll,
} from "features/chart";
import { useHistoricalData } from "components/Chart";
import ChartClock from "./ChartClock";
import { ServerBasePrice } from "services/types";
import { useTimeframe } from "shared/model";
import { getExpiryTimestamp } from "shared/utils";
import { ChevronIcon } from "shared/ui";
import { TradeMarkers } from "entities/trade";
import { selectIsCurrentPairMarketOpen } from "store/slices/tradingSlice";

import styles from "./Chart.module.css";

export type ChartProps = {
  symbol: string;
  chartType: "line" | "candlestick" | "histogram";
  onReady?: () => void;
  onFrame?: (data: ServerBasePrice[], zoom: ZoomRange) => void;
};

export const Chart: React.FC<ChartProps> = ({
  symbol = "BNBUSDT",
  chartType = "candlestick",
  onReady,
  onFrame,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const priceRangeRef = useRef<{ min: number; max: number } | null>(null);

  // DOM refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const markersLayerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const scheduleRedrawRef = useRef<() => void>(() => {});

  // Data ref
  const dataRef = useRef<ServerBasePrice[] | CandleWithFlag[]>([]);

  // Zoom state
  const [userZoomed, setUserZoomed] = useState(false);
  const [zoomRange, setZoomRange] = useState<ZoomRange>({ start: 0, end: 0 });
  const zoomRangeRef = useRef(zoomRange);
  const chartLayoutRef = useRef({ width: 0, height: 0, ...GRID });
  const prevLayoutSizeRef = useRef({ width: 0, height: 0 });
  const prevCenterCandleIndexRef = useRef<number | null>(null);
  const prevVisibleCandleCountRef = useRef<number | null>(null);
  const drawingInstancesRef = useRef<DrawingToolInstance[]>([]);
  const [layoutVersion, setLayoutVersion] = useState(0);

  // Interaction refs
  const crosshairPos = useRef<CrosshairPosition | null>(null);
  const hoverCandleIndex = useRef<number | null>(null);
  const autoScrollEnabledRef = useRef(true);

  // Scroll animation ref
  const scrollRafRef = useRef<number | null>(null);

  // Animation refs
  const animatedPriceRef = useRef<number | null>(null);
  const animationStartTimeRef = useRef<number>(0);
  const animatingCandleIndexRef = useRef<number | null>(null);
  const liveLinePriceRef = useRef<number | null>(null);
  const liveCandlePrice = useRef<number | null>(null);
  const prevLivePriceRef = useRef<number | null>(null);
  const prevHighRef = useRef<number | null>(null);
  const prevLowRef = useRef<number | null>(null);
  const highChangedRef = useRef(false);
  const lowChangedRef = useRef(false);

  // Server time animation refs
  const animatedNowRef = useRef(Date.now());
  const targetNowRef = useRef(Date.now());
  const timeAnimationStartRef = useRef(0);

  const [activeIndicators, setActiveIndicators] = useState<IndicatorType[]>([]);
  const [drawingCursor, setDrawingCursor] = useState<string | null>(null);

  // Redux
  const { selectedTimeframe, orderDuration, switchTimeEnabled } = useSelector(
    (state: RootState) => state.trading,
  );
  const orders = useSelector((state: RootState) => state.trading.orders);
  const isMarketOpen = useSelector((state: RootState) =>
    selectIsCurrentPairMarketOpen(state.trading),
  );
  const drawingInstances = useSelector(selectDrawingInstances);
  drawingInstancesRef.current = drawingInstances;
  const { timeframeMs } = useTimeframe();

  const firstVisibleMsRef = useRef<number>(0);
  const timeframeMsRef = useRef<number>(timeframeMs);
  timeframeMsRef.current = timeframeMs;

  useEffect(() => {
    dispatch(updateTimeframeMs(timeframeMs));
  }, [timeframeMs, dispatch]);

  const initialVisible = 100;
  const { getRightPaddingInCandles } = useRightPadding(initialVisible);

  const cancelScrollAnim = useCallback(() => {
    if (scrollRafRef.current !== null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  }, []);

  const smoothScrollTo = useCallback((newStart: number, newEnd: number) => {
    cancelScrollAnim();
    const DURATION = 400;
    const startTime = performance.now();
    const fromStart = zoomRangeRef.current.start;
    const fromEnd = zoomRangeRef.current.end;

    const animate = (now: number) => {
      const t = Math.min((now - startTime) / DURATION, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setZoomRange({
        start: fromStart + (newStart - fromStart) * eased,
        end: fromEnd + (newEnd - fromEnd) * eased,
      });
      if (t < 1) {
        scrollRafRef.current = requestAnimationFrame(animate);
      } else {
        scrollRafRef.current = null;
      }
    };
    scrollRafRef.current = requestAnimationFrame(animate);
  }, [cancelScrollAnim]);
  const { data: historyData, isLoading: historyLoading } = useHistoricalData(
    symbol,
    selectedTimeframe,
    chartType,
  );

  const hasCalledOnReady = useRef(false);
  useEffect(() => {
    if (!historyLoading && historyData?.length && !hasCalledOnReady.current) {
      hasCalledOnReady.current = true;
      onReady?.();
    }
  }, [historyLoading, historyData, onReady]);

  // ── Sync history data ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!historyData?.length) return;
    dataRef.current = [...historyData];
    liveCandlePrice.current = null;
    prevLivePriceRef.current = null;
    animatedPriceRef.current = null;
    animatingCandleIndexRef.current = null;
    scheduleRedrawRef.current();
  }, [historyData]);

  // Update ref synchronously during render so TradeMarkers positions are always
  // computed from the current zoom value, not the previous render's value.
  zoomRangeRef.current = zoomRange;

  // Trigger canvas redraw on zoom/pan
  useEffect(() => {
    scheduleRedrawRef.current();
  }, [zoomRange]);

  useEffect(() => {
    scheduleRedrawRef.current();
  }, [drawingInstances]);

  // ── Zoom effects ──────────────────────────────────────────────────────────
  // Fires only when zoom state resets (user un-zooms), NOT on every new candle
  useEffect(() => {
    if (dataRef.current.length === 0 || !containerRef.current) return;
    if (!userZoomed) {
      const containerWidth = containerRef.current.clientWidth;
      const rightPadding = getRightPaddingInCandles(containerWidth);
      const lastCandleIndex = dataRef.current.length - 1;
      const end = lastCandleIndex + rightPadding;
      const start = Math.max(0, end - initialVisible + 1);
      cancelScrollAnim();
      setZoomRange({ start, end });
    }
  }, [
    userZoomed,
    initialVisible,
    getRightPaddingInCandles,
    cancelScrollAnim,
  ]);

  // Auto-scroll: fires on new candle, scrolls when last candle enters the price-label zone
  useEffect(() => {
    if (dataRef.current.length === 0 || !containerRef.current) return;
    if (!autoScrollEnabledRef.current) return;

    const containerWidth = containerRef.current.clientWidth;
    const chartWidth = containerWidth - GRID.left - GRID.right;
    const visibleCandles = zoomRangeRef.current.end - zoomRangeRef.current.start + 1;
    const candleWidth = chartWidth / visibleCandles;
    const priceScaleBuffer = Math.ceil((GRID.right + 65) / candleWidth);

    const lastCandleIndex = dataRef.current.length - 1;
    if (lastCandleIndex < zoomRangeRef.current.end - priceScaleBuffer) return;

    // Use current zoom's candle width for right padding so zoomed views stay on screen
    const rightPadding = Math.ceil((chartWidth * RIGHT_PADDING_PERCENT) / candleWidth);
    const newEnd = lastCandleIndex + rightPadding;
    const newStart = userZoomed
      ? Math.max(0, newEnd - (zoomRangeRef.current.end - zoomRangeRef.current.start))
      : Math.max(0, newEnd - initialVisible + 1);

    smoothScrollTo(newStart, newEnd);
  }, [
    dataRef.current.length,
    initialVisible,
    userZoomed,
    smoothScrollTo,
  ]);

  useEffect(() => {
    if (!historyData?.length || !containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const rightPadding = getRightPaddingInCandles(containerWidth);
    const lastIndex = historyData.length - 1;
    const end = lastIndex + rightPadding;
    const start = Math.max(0, end - initialVisible + 1);
    setZoomRange({ start, end });
  }, [historyData]);

  // ── Animate server time ───────────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      const now = websocketService.getCurrentServerTime();
      animatedNowRef.current = targetNowRef.current;
      targetNowRef.current = now;
      timeAnimationStartRef.current = performance.now();
      scheduleRedrawRef.current(); // starts rAF loop for 300ms transition
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const getAnimatedNow = () => {
    if (timeAnimationStartRef.current === 0) return targetNowRef.current;
    const elapsed = performance.now() - timeAnimationStartRef.current;
    const progress = Math.min(elapsed / 300, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    const value =
      animatedNowRef.current +
      (targetNowRef.current - animatedNowRef.current) * eased;
    if (progress >= 1) timeAnimationStartRef.current = 0;
    return value;
  };

  // ── Coordinate helpers (passed to TradeMarkers) ───────────────────────────
  const getTimeX = useCallback((candleIndex: number): number | null => {
    const { start, end } = zoomRangeRef.current;
    const layout = chartLayoutRef.current;
    if (candleIndex < start || candleIndex > end) return null;
    const visibleIndex = candleIndex - Math.floor(start);
    const chartWidth = layout.width - layout.left - layout.right;
    const visibleCount = Math.floor(end) - Math.floor(start) + 1;
    const candleWidth = chartWidth / visibleCount;
    return layout.left + (visibleIndex + 0.5) * candleWidth;
  }, []);

  const getPriceY = useCallback((price: number): number | null => {
    const dataArr = dataRef.current;
    if (!dataArr.length) return null;
    const { start, end } = zoomRangeRef.current;
    const layout = chartLayoutRef.current;
    const chartHeight = layout.height - layout.top - layout.bottom;
    let minPrice = Infinity;
    let maxPrice = -Infinity;
    const startIndex = Math.floor(start);
    const endIndex = Math.min(Math.floor(end), dataArr.length - 1);
    for (let i = startIndex; i <= endIndex; i++) {
      const c: any = dataArr[i];
      if (!c) continue;
      if ("low" in c && "high" in c) {
        minPrice = Math.min(minPrice, c.low);
        maxPrice = Math.max(maxPrice, c.high);
      } else if ("price" in c) {
        minPrice = Math.min(minPrice, c.price);
        maxPrice = Math.max(maxPrice, c.price);
      }
    }
    if (minPrice === Infinity) return null;
    const priceRange = maxPrice - minPrice || 1;
    const padding = priceRange * 0.05;
    minPrice -= padding;
    maxPrice += padding;
    return (
      layout.top + ((maxPrice - price) / (maxPrice - minPrice)) * chartHeight
    );
  }, []);

  const findCandleIndex = useCallback((timestamp: number): number | null => {
    const dataArr = dataRef.current;
    for (let i = dataArr.length - 1; i >= 0; i--) {
      const candleTime = Date.parse((dataArr[i] as any).timestamp);
      if (timestamp >= candleTime) return i;
    }
    return null;
  }, []);

  const timeToXCallback = useCallback(
    (timestamp: number): number | null => {
      const dataArr = dataRef.current;
      if (!dataArr.length) return null;
      const { start, end } = zoomRangeRef.current;
      const layout = chartLayoutRef.current;
      const firstVisible = dataArr[Math.floor(start)];
      if (!firstVisible) return null;
      const firstVisibleTime = new Date(firstVisible.timestamp).getTime();
      const chartWidth = layout.width - layout.left - layout.right;
      const visibleCount = Math.floor(end) - Math.floor(start) + 1;
      const candleWidth = chartWidth / visibleCount;
      const msFromStart = timestamp - firstVisibleTime;
      const indexFloat = msFromStart / timeframeMs;
      return layout.left + (indexFloat + 0.5) * candleWidth;
    },
    [timeframeMs],
  );

  // ── Hooks ─────────────────────────────────────────────────────────────────
  const {
    isDragging,
    handleMouseMove,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
    handleWheel,
  } = useChartInteraction({
    canvasRef,
    containerRef,
    zoomRangeRef,
    chartLayoutRef,
    dataRef,
    crosshairPosRef: crosshairPos,
    hoverCandleIndexRef: hoverCandleIndex,
    autoScrollEnabledRef,
    setZoomRange,
    setUserZoomed,
    onNeedsRedraw: () => scheduleRedrawRef.current(),
  });

  useChartWebSocket({
    symbol,
    timeframeMs,
    chartType,
    dataRef,
    animatedPriceRef,
    animationStartTimeRef,
    animatingCandleIndexRef,
    liveCandlePrice,
    liveLinePriceRef,
    prevHighRef,
    prevLowRef,
    highChangedRef,
    lowChangedRef,
    onTick: () => scheduleRedrawRef.current(),
  });

  useCandleOpenTicker({
    symbol,
    chartType,
    timeframeMs,
    dataRef,
    onCandleOpen: () => {
      const candles = dataRef.current as CandleWithFlag[];
      const last = candles[candles.length - 1];
      if (last) {
        liveCandlePrice.current = last.close;
        animatedPriceRef.current = last.close;
        animationStartTimeRef.current = performance.now();
        animatingCandleIndexRef.current = candles.length - 1;
      }
      scheduleRedrawRef.current();
    },
  });

  const { showScrollButton, scrollToLastCandle } = useChartAutoScroll({
    dataRef,
    containerRef,
    zoomRange,
    zoomRangeRef,
    autoScrollEnabledRef,
    smoothScrollTo,
  });

  const {
    draggingOverrideRef,
    onMouseDown: onDrawingMouseDown,
    onMouseMove: onDrawingMouseMove,
    onMouseUp: onDrawingMouseUp,
    getCursor: getDrawingCursor,
  } = useDrawingToolDrag(
    chartLayoutRef,
    priceRangeRef,
    zoomRangeRef,
    firstVisibleMsRef,
    timeframeMsRef,
    drawingInstancesRef,
    () => scheduleRedrawRef.current(),
  );

  // ── Drawing tool mouse wrappers ───────────────────────────────────────────
  const getCanvasMousePos = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const { x, y } = getCanvasMousePos(e);
      const cursor = onDrawingMouseDown(x, y);
      if (cursor) {
        setDrawingCursor(cursor);
        return;
      }
      handleMouseDown(e);
    },
    [getCanvasMousePos, onDrawingMouseDown, handleMouseDown],
  );

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const { x, y } = getCanvasMousePos(e);
      onDrawingMouseMove(x, y);
      setDrawingCursor(getDrawingCursor(x, y));
      handleMouseMove(e);
    },
    [getCanvasMousePos, onDrawingMouseMove, getDrawingCursor, handleMouseMove],
  );

  const handleCanvasMouseUp = useCallback(() => {
    onDrawingMouseUp();
    setDrawingCursor(null);
    handleMouseUp();
  }, [onDrawingMouseUp, handleMouseUp]);

  const handleCanvasMouseLeave = useCallback(() => {
    setDrawingCursor(null);
    handleMouseLeave();
  }, [handleMouseLeave]);

  // ── Draw loop ─────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const container = containerRef.current;
    if (!canvas || !ctx || !container) return;

    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    canvas.width = rect.width * devicePixelRatio;
    canvas.height = rect.height * devicePixelRatio;
    ctx.scale(devicePixelRatio, devicePixelRatio);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    const layout = {
      width: rect.width,
      height: rect.height,
      left: GRID.left,
      right: GRID.right,
      top: GRID.top,
      bottom: GRID.bottom,
    };
    chartLayoutRef.current = layout;
    if (layout.width !== prevLayoutSizeRef.current.width || layout.height !== prevLayoutSizeRef.current.height) {
      prevLayoutSizeRef.current = { width: layout.width, height: layout.height };
      setLayoutVersion((v) => v + 1);
    }

    const { width: w, height: h, left, right, top, bottom } = layout;
    const chartWidth = w - left - right;
    const chartHeight = h - top - bottom;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, w, h);

    const dataArr = dataRef.current;
    if (!dataArr.length) return;

    const { start, end } = zoomRangeRef.current;
    const visibleCount = Math.floor(end) - Math.floor(start) + 1;
    if (visibleCount <= 0) return;

    const candleWidth = Math.max(CANDLE_MIN_WIDTH, chartWidth / visibleCount);
    const candleGap = Math.min(2, candleWidth * 0.1);
    const candleBodyWidth = candleWidth - candleGap * 2;
    const startIndex = Math.floor(start);
    const endIndex = Math.min(Math.floor(end), dataArr.length - 1);

    // Guard: zoomRange is stale (data just replaced with fewer candles — skip frame)
    if (startIndex >= dataArr.length) {
      animationRef.current = requestAnimationFrame(draw);
      return;
    }

    // Compute price range
    let minPrice = Infinity;
    let maxPrice = -Infinity;
    for (let i = startIndex; i <= endIndex; i++) {
      if (i >= dataArr.length) break;
      const c = dataArr[i];
      if (!c) continue;
      if (chartType === "line") {
        if (!("price" in c)) continue;
        minPrice = Math.min(minPrice, c.price);
        maxPrice = Math.max(maxPrice, c.price);
      } else {
        if (!("low" in c) || !("high" in c)) continue;
        minPrice = Math.min(minPrice, c.low);
        maxPrice = Math.max(maxPrice, c.high);
      }
    }
    if (minPrice === Infinity && dataArr.length > 0) {
      const last = dataArr[dataArr.length - 1];
      if (chartType === "line") {
        minPrice = maxPrice = "price" in last ? last.price : 0;
        if (minPrice === 0) maxPrice = 1;
      } else if ("low" in last && "high" in last) {
        minPrice = last.low;
        maxPrice = last.high;
      } else {
        minPrice = 0;
        maxPrice = 1;
      }
    }

    const priceRange = maxPrice - minPrice || 1;
    const pricePadding = priceRange * 0.05;
    minPrice -= pricePadding;
    maxPrice += pricePadding;

    if (
      priceRangeRef.current?.min !== minPrice ||
      priceRangeRef.current?.max !== maxPrice
    ) {
      priceRangeRef.current = { min: minPrice, max: maxPrice };
      dispatch(updateChartPriceRange({ min: minPrice, max: maxPrice }));
    }

    const centerCandleIndex = Math.round((start + end) / 2);
    if (prevCenterCandleIndexRef.current !== centerCandleIndex) {
      prevCenterCandleIndexRef.current = centerCandleIndex;
      const centerCandle = dataArr[Math.min(centerCandleIndex, dataArr.length - 1)];
      if (centerCandle) dispatch(updateVerticalLineInitialTimestamp(new Date(centerCandle.timestamp).getTime()));
    }
    if (prevVisibleCandleCountRef.current !== visibleCount) {
      prevVisibleCandleCountRef.current = visibleCount;
      dispatch(updateVisibleCandleCount(visibleCount));
    }

    const priceToY = (price: number) =>
      top + ((maxPrice - price) / (maxPrice - minPrice)) * chartHeight;

    drawGrid(
      ctx,
      rect,
      maxPrice,
      minPrice,
      priceToY,
      visibleCount,
      candleWidth,
      startIndex,
      dataArr,
    );

    // Compute animated line price
    let animatedLinePrice: number | null = null;
    if (
      animationStartTimeRef.current > 0 &&
      liveLinePriceRef.current !== null
    ) {
      const elapsed = performance.now() - animationStartTimeRef.current;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const from = animatedPriceRef.current ?? liveLinePriceRef.current;
      animatedLinePrice = from + (liveLinePriceRef.current - from) * eased;
      if (progress >= 1) {
        animationStartTimeRef.current = 0;
        animatedPriceRef.current = liveLinePriceRef.current;
      }
    }

    // Compute animated candle values
    const animatedValues = new Map<
      number,
      { close: number; high: number; low: number }
    >();
    if (
      animationStartTimeRef.current > 0 &&
      animatingCandleIndexRef.current !== null
    ) {
      const elapsed = performance.now() - animationStartTimeRef.current;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const candleIndex = animatingCandleIndexRef.current;
      if (candleIndex < dataArr.length) {
        const candle = dataArr[candleIndex] as CandleWithFlag;
        if ("close" in candle) {
          const startPrice = animatedPriceRef.current ?? candle.close;
          const targetPrice = liveCandlePrice.current ?? candle.close;
          animatedValues.set(candleIndex, {
            close: startPrice + (targetPrice - startPrice) * eased,
            high: highChangedRef.current
              ? prevHighRef.current! +
                (candle.high - prevHighRef.current!) * eased
              : candle.high,
            low: lowChangedRef.current
              ? prevLowRef.current! + (candle.low - prevLowRef.current!) * eased
              : candle.low,
          });
          if (progress >= 1) {
            animatedPriceRef.current = targetPrice;
            animationStartTimeRef.current = 0;
            animatingCandleIndexRef.current = null;
            if ((candle as CandleWithFlag).isNew)
              (candle as CandleWithFlag).isNew = false;
          }
        }
      }
    }

    // Draw chart type
    if (chartType === "line") {
      drawLine(
        ctx,
        dataArr,
        startIndex,
        visibleCount,
        left,
        candleWidth,
        priceToY,
        animatedLinePrice,
      );
    } else if (chartType === "candlestick") {
      drawCandles(
        ctx,
        dataArr as CandleWithFlag[],
        startIndex,
        visibleCount,
        left,
        candleWidth,
        candleBodyWidth,
        priceToY,
        animatedValues,
      );
    } else if (chartType === "histogram") {
      drawHistogram(
        ctx,
        dataArr as CandleWithFlag[],
        startIndex,
        visibleCount,
        left,
        candleWidth,
        priceToY,
        animatedValues,
      );
    }

    // Drawing tools
    const firstVisibleMs = new Date(dataArr[startIndex].timestamp).getTime();
    firstVisibleMsRef.current = firstVisibleMs;
    const tsToX = (ts: number) => timestampToX(ts, layout, zoomRangeRef.current, firstVisibleMs, timeframeMs);

    drawDrawingInstances(ctx, drawingInstancesRef.current, layout, tsToX, priceToY, draggingOverrideRef.current);

    // Expiry line
    const animatedNow = getAnimatedNow();
    // В режиме Switch Time линия привязана к минуте: двигается только когда
    // меняется отображаемая минута, а не каждую секунду вслед за server time.
    // Тот же расчёт уходит в close_timestamp ордера — см. getExpiryTimestamp.
    const expiryTime = getExpiryTimestamp(
      animatedNow,
      orderDuration,
      switchTimeEnabled,
    );
    const timeToXLocal = (timestamp: number) => {
      const { start } = zoomRangeRef.current;
      const firstVisibleTime = new Date(
        dataArr[Math.floor(start)].timestamp,
      ).getTime();
      const msFromStart = timestamp - firstVisibleTime;
      const indexFloat = msFromStart / timeframeMs;
      return left + (indexFloat + 0.5) * candleWidth;
    };
    drawExpiryLine(ctx, expiryTime, layout, timeToXLocal, switchTimeEnabled);

    // Live price line
    let currentPrice: number | null = null;
    if (chartType === "line") {
      currentPrice = animatedLinePrice ?? liveLinePriceRef.current;
    } else if (
      animationStartTimeRef.current > 0 &&
      animatingCandleIndexRef.current !== null
    ) {
      const elapsed = performance.now() - animationStartTimeRef.current;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const from = animatedPriceRef.current ?? liveCandlePrice.current ?? 0;
      currentPrice = from + ((liveCandlePrice.current ?? from) - from) * eased;
    } else {
      currentPrice = liveCandlePrice.current;
    }
    if (currentPrice === null && dataArr.length) {
      const last = dataArr[dataArr.length - 1] as any;
      currentPrice = last.price ?? last.close ?? null;
    }
    if (currentPrice !== null) {
      drawLivePriceLine(ctx, currentPrice, priceToY, layout);
    }

    // Crosshair
    if (crosshairPos.current && hoverCandleIndex.current !== null) {
      drawCrosshair(
        ctx,
        crosshairPos.current,
        hoverCandleIndex.current,
        dataArr,
        layout,
      );
    }

    onFrame?.(dataArr, { ...zoomRangeRef.current });

    // Continue loop only while animations are active
    const hasActiveAnimation =
      animationStartTimeRef.current > 0 || timeAnimationStartRef.current > 0;

    if (hasActiveAnimation) {
      animationRef.current = requestAnimationFrame(draw);
    } else {
      animationRef.current = null;
    }
  }, [chartType, historyData, orderDuration, switchTimeEnabled, timeframeMs]);

  // scheduleRedraw: trigger a single frame if no loop is running
  const scheduleRedraw = useCallback(() => {
    if (animationRef.current !== null) return;
    animationRef.current = requestAnimationFrame(draw);
  }, [draw]);

  // Keep ref in sync so effects/hooks can call it without stale closure
  scheduleRedrawRef.current = scheduleRedraw;

  useEffect(() => {
    draw();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [draw]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleToggleIndicator = (indicator: IndicatorType) => {
    setActiveIndicators((prev) =>
      prev.includes(indicator)
        ? prev.filter((i) => i !== indicator)
        : [...prev, indicator],
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.chart} ref={containerRef}>
      <ChartClock />
      {showScrollButton && (
        <button className={styles.btn} onClick={scrollToLastCandle}>
          <ChevronIcon
            fill="#ffffff"
            width={16}
            height={8}
            className={styles.chevron}
            direction="right"
          />
        </button>
      )}
      {/* <div className={styles.indicators}>
        <ChartIndicators
          activeIndicators={activeIndicators}
          onToggleIndicator={handleToggleIndicator}
        />
      </div> */}
      {historyLoading && <Spiner />}
      {historyData && (
        <>
          <canvas
            ref={canvasRef}
            style={{
              width: "100%",
              height: "100%",
              display: "block",
              cursor: drawingCursor ?? (isDragging ? "default" : "crosshair"),
              filter: isMarketOpen ? undefined : "blur(6px)",
            }}
            onMouseMove={handleCanvasMouseMove}
            onMouseLeave={handleCanvasMouseLeave}
            onMouseDown={handleCanvasMouseDown}
            onMouseUp={handleCanvasMouseUp}
            onWheel={handleWheel}
          />
          {historyData?.length > 0 && (
            <div
              ref={markersLayerRef}
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                zIndex: 5,
                filter: isMarketOpen ? undefined : "blur(6px)",
              }}
            >
              <TradeMarkers
                orders={orders}
                symbol={symbol}
                getTimeX={getTimeX}
                getPriceY={getPriceY}
                findCandleIndex={findCandleIndex}
                zoomRange={zoomRange}
                timeToX={timeToXCallback}
                layoutVersion={layoutVersion}
              />
            </div>
          )}
        </>
      )}
      {!isMarketOpen && <MarketClosedOverlay />}
    </div>
  );
};
